import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import type { PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { registerNotificationDevice } from '@/api/notificationApi';
import { getBaseUrl } from '@/config/environment';
import { setNotificationAttribution } from '@/services/notificationAttribution';
import { adtraceService } from '@/services/adtraceService';

const PENDING_PAYMENT_ROUTE_KEY = 'booka_pending_payment_route';
const PENDING_NOTIFICATION_ROUTE_KEY = 'booka_pending_notification_route';
const handledNotificationUrls = new Set<string>();
// Capacitor can keep returning the original cold-start URL from getLaunchUrl()
// after the notification listener effect is recreated (for example when auth
// finishes bootstrapping). Keep a process-lifetime record so that old launch
// intents are never replayed as fresh navigation events.
const handledLaunchUrls = new Set<string>();

interface NotificationManagerProps {
  onNotificationReceived?: (notification: PushNotificationSchema) => void;
  onNotificationClicked?: (action: ActionPerformed) => void;
  /** The app may receive a deep link before AuthContext finishes bootstrapping. */
  canOpenProtectedRoutes?: boolean;
  /** Device registration must only happen after a fully registered user is known. */
  canRegisterDevice?: boolean;
}

export function NotificationManager({
  onNotificationReceived,
  onNotificationClicked,
  canOpenProtectedRoutes = false,
  canRegisterDevice = false,
}: NotificationManagerProps) {
  const navigate = useNavigate();
  const latestCanOpenProtectedRoutes = useRef(canOpenProtectedRoutes);
  const pendingNotification = useRef<{
    route: string;
    routeState?: string | null;
    campaign?: string;
  } | null>(null);
  const loggedNotificationOpens = useRef(new Set<string>());

  // Keep the latest auth state available to native callbacks without re-registering
  // Capacitor listeners every time AuthContext changes during app startup.
  latestCanOpenProtectedRoutes.current = canOpenProtectedRoutes;

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      console.log('[NotificationManager] Not a native platform, skipping initialization');
      return;
    }

    let notificationService: any = null;
    let didReceiveRealToken = false;
    const cleanupTasks: Array<() => void> = [];

    const isLocalApiBaseUrl = () => {
      try {
        const { hostname } = new URL(getBaseUrl());
        return (
          hostname === 'localhost' ||
          hostname === '127.0.0.1' ||
          hostname.startsWith('192.168.') ||
          hostname.startsWith('10.') ||
          /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname)
        );
      } catch {
        return false;
      }
    };

    const getDeviceRegistrationContext = async () => {
      const [deviceInfo, deviceId] = await Promise.all([
        Device.getInfo(),
        Device.getId().catch(() => null)
      ]);

      const platform = deviceInfo.platform === 'ios'
        ? 'ios'
        : deviceInfo.platform === 'android'
          ? 'android'
          : 'web';

      return {
        platform,
        deviceId: deviceId?.identifier || null
      };
    };

    const registerDeviceToken = async (token: string) => {
      try {
        const { platform, deviceId } = await getDeviceRegistrationContext();

        const registeredDevice = await registerNotificationDevice({
          token,
          platform,
          device_id: deviceId
        });

        console.log('Notification device registered:', registeredDevice);
      } catch (error) {
        console.error('Failed to register notification device:', error);
      }
    };

    const registerFakeDeviceTokenForLocalTesting = async () => {
      if (didReceiveRealToken || !isLocalApiBaseUrl()) {
        return;
      }

      try {
        const { deviceId } = await getDeviceRegistrationContext();
        const fakeToken = `dev-fake-fcm-token:${deviceId || 'unknown-device'}:${Date.now()}`;

        console.warn('Registering local test notification device with fake FCM token:', fakeToken);
        await registerDeviceToken(fakeToken);
      } catch (error) {
        console.error('Failed to register fake notification device:', error);
      }
    };

    const hasHandledNotificationUrl = (url: string) => (
      handledNotificationUrls.has(url)
    );

    const markNotificationUrlAsHandled = (url: string) => {
      handledNotificationUrls.add(url);
      // Ignore duplicate callbacks for the same tap, but allow a later
      // notification with the same campaign/route to be counted again.
      window.setTimeout(() => handledNotificationUrls.delete(url), 3000);
    };

    const navigateToNotificationRoute = (
      route: string,
      routeStateRaw?: string | null,
      campaign?: string,
    ) => {
      let routeState: Record<string, any> | undefined;

      if (routeStateRaw) {
        try {
          routeState = JSON.parse(routeStateRaw);
        } catch (error) {
          console.warn('Failed to parse notification route_state:', error);
        }
      }

      navigate(route, {
        state: {
          ...(routeState || {}),
          source: 'notification',
          notificationCampaign: campaign || undefined,
        },
      });
    };

    const persistPendingPaymentRoute = (route: string) => {
      try {
        localStorage.setItem(PENDING_PAYMENT_ROUTE_KEY, route);
        window.dispatchEvent(new CustomEvent('booka:pending-payment-route', {
          detail: route,
        }));
      } catch {
        // The in-memory queue below still handles the current app process.
      }
    };

    const readPendingPaymentRoute = () => {
      try {
        return localStorage.getItem(PENDING_PAYMENT_ROUTE_KEY);
      } catch {
        return null;
      }
    };

    const clearPendingPaymentRoute = () => {
      try {
        localStorage.removeItem(PENDING_PAYMENT_ROUTE_KEY);
      } catch {
        // Ignore storage failures after the route has been handed to the app.
      }
    };

    const persistPendingNotificationRoute = (
      route: string,
      routeStateRaw?: string | null,
      campaign?: string,
    ) => {
      try {
        localStorage.setItem(PENDING_NOTIFICATION_ROUTE_KEY, JSON.stringify({ route, routeStateRaw, campaign }));
        window.dispatchEvent(new CustomEvent('booka:pending-notification-route', {
          detail: { route, routeStateRaw },
        }));
      } catch {
        // The in-memory queue below still handles the current app process.
      }
    };

    const readPendingNotificationRoute = () => {
      try {
        const value = localStorage.getItem(PENDING_NOTIFICATION_ROUTE_KEY);
        if (!value) return null;
        const parsed = JSON.parse(value);
      return typeof parsed?.route === 'string'
        ? parsed as { route: string; routeStateRaw?: string | null; campaign?: string }
        : null;
      } catch {
        return null;
      }
    };

    const clearPendingNotificationRoute = () => {
      try { localStorage.removeItem(PENDING_NOTIFICATION_ROUTE_KEY); } catch { /* ignore */ }
    };

    const openOrQueueNotificationRoute = (
      route: string,
      routeStateRaw?: string | null,
      campaign?: string,
    ) => {
      if (!latestCanOpenProtectedRoutes.current) {
        pendingNotification.current = { route, routeState: routeStateRaw, campaign };
        persistPendingNotificationRoute(route, routeStateRaw, campaign);
        console.log('[NotificationManager] Auth is not ready; queued notification route:', route);
        return;
      }

      if (route.startsWith('/payment-confirmation?')) {
        clearPendingPaymentRoute();
      }
      clearPendingNotificationRoute();
      navigateToNotificationRoute(route, routeStateRaw, campaign);
    };

    const logNotificationOpen = (input: {
      campaign?: unknown;
      targetRoute?: unknown;
      ctaLabel?: unknown;
      action?: unknown;
      routeStateRaw?: unknown;
      notificationId?: unknown;
    }) => {
      const campaign = typeof input.campaign === 'string' && input.campaign.trim()
        ? input.campaign.trim()
        : 'unknown';
      const targetRoute = typeof input.targetRoute === 'string' && input.targetRoute
        ? input.targetRoute
        : '/dashboard';
      const action = input.action === 'cta' ? 'cta' : 'notification';
      const dedupeKey = input.notificationId
        ? `${String(input.notificationId)}|${action}`
        : `${campaign}|${targetRoute}|${action}|${String(input.routeStateRaw || '')}`;

      // Android can deliver both the native action callback and appUrlOpen for
      // one tap. Count that tap once.
      if (loggedNotificationOpens.current.has(dedupeKey)) return;
      loggedNotificationOpens.current.add(dedupeKey);

      setNotificationAttribution({
        campaign,
        targetRoute,
        targetBookId: (() => {
          if (typeof input.routeStateRaw !== 'string') return undefined;
          try {
            const parsedBookId = Number(JSON.parse(input.routeStateRaw)?.bookId);
            return Number.isInteger(parsedBookId) && parsedBookId > 0 ? parsedBookId : undefined;
          } catch {
            return undefined;
          }
        })(),
      });

      // The Android layer logs booka_notification_opened because the custom
      // campaign notification is opened through a native PendingIntent. Do
      // not send notification_open here: it is a Firebase-reserved event name
      // and would also duplicate the native event.
    };

    const navigateFromNotificationData = (data: Record<string, any> | undefined) => {
      if (!data?.route) {
        return;
      }

      openOrQueueNotificationRoute(
        data.route,
        data.route_state,
        typeof data.campaign_type === 'string' ? data.campaign_type : data.type,
      );
    };

    const navigateFromNotificationUrl = (url: string | undefined) => {
      if (!url) {
        return;
      }

      if (hasHandledNotificationUrl(url)) {
        console.log('Skipping already handled notification deep link:', url);
        return;
      }

      try {
        const parsedUrl = new URL(url);

        if (parsedUrl.protocol === 'booka:' && parsedUrl.hostname === 'attribution') {
          // Forward only Adtrace tracker deep links for re-attribution.
          void adtraceService.appWillOpenUrl(url).catch(() => {});
          void adtraceService.getAttribution().then((attribution) => {
            if (!attribution) return;
            try {
              localStorage.setItem('booka_adtrace_attribution', JSON.stringify({
                ...attribution,
                receivedAt: Date.now(),
              }));
            } catch { /* attribution persistence must never block navigation */ }
          });
          markNotificationUrlAsHandled(url);
          return;
        }

        if (parsedUrl.protocol === 'booka:' && parsedUrl.hostname === 'payment') {
          markNotificationUrlAsHandled(url);

          const status = parsedUrl.searchParams.get('status') || 'failed';
          const trackId = parsedUrl.searchParams.get('track_id') || '';
          const orderId = parsedUrl.searchParams.get('order_id') || '';
          const query = new URLSearchParams({ status, track_id: trackId });
          if (orderId) query.set('order_id', orderId);

          const paymentRoute = '/payment-confirmation?' + query.toString();
          persistPendingPaymentRoute(paymentRoute);
          openOrQueueNotificationRoute(paymentRoute);
          return;
        }

        if (parsedUrl.protocol !== 'booka:' || parsedUrl.hostname !== 'notification') {
          return;
        }

        markNotificationUrlAsHandled(url);
        const targetRoute = parsedUrl.searchParams.get('route') || '/dashboard';
        const routeStateRaw = parsedUrl.searchParams.get('route_state');
        logNotificationOpen({
          campaign: parsedUrl.searchParams.get('campaign_type') || parsedUrl.searchParams.get('type'),
          targetRoute,
          ctaLabel: parsedUrl.searchParams.get('cta_label'),
          action: parsedUrl.searchParams.get('source'),
          routeStateRaw,
          notificationId: parsedUrl.searchParams.get('notification_id'),
        });
        openOrQueueNotificationRoute(
          targetRoute,
          routeStateRaw,
          parsedUrl.searchParams.get('campaign_type') || parsedUrl.searchParams.get('type') || 'unknown',
        );
      } catch (error) {
        console.warn('Failed to handle notification deep link:', error);
      }
    };

    const initializeNotifications = async () => {
      try {
        console.log('[NotificationManager] Starting dashboard notification flow...');

        const notifModule = await import('@/services/notificationService');
        notificationService = notifModule.notificationService;

        await notificationService.initialize({
          onTokenReceived: (token: string) => {
            didReceiveRealToken = true;
            void adtraceService.setPushToken(token).catch((error) => {
              console.warn('[Adtrace] Failed to send FCM token for uninstall tracking:', error);
            });
            void registerDeviceToken(token);
          },
          onRegistrationError: () => {
            void registerFakeDeviceTokenForLocalTesting();
          },
          onNotificationReceived: (notification: PushNotificationSchema) => {
            console.log('Notification received:', notification);

            if (onNotificationReceived) {
              onNotificationReceived(notification);
            }
          },
          onNotificationActionPerformed: (action: ActionPerformed) => {
            console.log('Notification clicked:', action);

            const data = action.notification.data || {};
            const campaign = typeof data.campaign_type === 'string'
              ? data.campaign_type
              : typeof data.type === 'string' ? data.type : 'unknown';
            const targetRoute = typeof data.route === 'string' ? data.route : '/dashboard';

            logNotificationOpen({
              campaign,
              targetRoute,
              ctaLabel: data.cta_label,
              action: data.source,
              routeStateRaw: data.route_state,
              notificationId: data.notification_id,
            });

            if (onNotificationClicked) {
              onNotificationClicked(action);
            }

            navigateFromNotificationData(data);
          },
        });

        console.log('NotificationManager finished dashboard notification flow');
      } catch (error) {
        console.error('Error initializing NotificationManager:', error);
      }
    };

    const readLaunchUrl = async (attempt: number) => {
      try {
        const launchUrl = await App.getLaunchUrl();
        console.log('[NotificationManager] Launch URL:', launchUrl?.url || '(none)', 'attempt:', attempt);
        if (launchUrl?.url && !handledLaunchUrls.has(launchUrl.url)) {
          handledLaunchUrls.add(launchUrl.url);
          navigateFromNotificationUrl(launchUrl.url);
        }
      } catch (error) {
        console.warn('[NotificationManager] Failed to read launch URL:', error);
      }
    };

    const appUrlOpenPromise = App.addListener('appUrlOpen', ({ url }) => {
      navigateFromNotificationUrl(url);
    });

    cleanupTasks.push(() => {
      void appUrlOpenPromise.then((listener) => listener.remove());
    });

    // On a cold Android start the bridge can expose the intent just after the
    // first JS tick. Read it again briefly so RootRedirect cannot win the race.
    void readLaunchUrl(1);
    const launchUrlRetryTimers = [700, 1800].map((delay, index) => (
      window.setTimeout(() => { void readLaunchUrl(index + 2); }, delay)
    ));

    // A payment callback can arrive while Android is starting the process. Keep
    // a durable route so it survives the auth bootstrap and process recreation.
    const pendingPaymentRoute = readPendingPaymentRoute();
    if (pendingPaymentRoute) {
      if (canOpenProtectedRoutes) {
        pendingNotification.current = null;
        clearPendingPaymentRoute();
        navigateToNotificationRoute(pendingPaymentRoute);
      } else {
        pendingNotification.current = { route: pendingPaymentRoute };
      }
    }

    const pendingNotificationRoute = readPendingNotificationRoute();
    if (pendingNotificationRoute && !canOpenProtectedRoutes) {
      pendingNotification.current = pendingNotificationRoute;
    } else if (pendingNotificationRoute && canOpenProtectedRoutes) {
      pendingNotification.current = null;
      clearPendingNotificationRoute();
      navigateToNotificationRoute(
        pendingNotificationRoute.route,
        pendingNotificationRoute.routeStateRaw,
        pendingNotificationRoute.campaign,
      );
    }

    const timeoutId = canRegisterDevice
      ? setTimeout(() => { void initializeNotifications(); }, 400)
      : undefined;

    // Replay a queued notification after AuthContext has loaded the persistent session.
    if (canOpenProtectedRoutes && pendingNotification.current) {
      const queued = pendingNotification.current;
      pendingNotification.current = null;
      navigateToNotificationRoute(queued.route, queued.routeState, queued.campaign);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      launchUrlRetryTimers.forEach((timer) => window.clearTimeout(timer));
      cleanupTasks.forEach((cleanupTask) => cleanupTask());
      if (notificationService) {
        notificationService.cleanup();
      }
    };
  }, [navigate, onNotificationReceived, onNotificationClicked, canRegisterDevice, canOpenProtectedRoutes]);

  return null;
}
